import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Users, Heart, Zap, BookOpen, Calendar } from "lucide-react";
import Seo from "../components/Seo";
import RotatingHero from "../components/RotatingHero";
import api from "../lib/api";

function UpcomingEventsSection() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => {
      const now = Date.now();
      const upcoming = (r.data || [])
        .filter((e) => e.feature_on_home && e.image_url && new Date(e.event_date).getTime() >= now)
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
        .slice(0, 6);
      setEvents(upcoming);
    }).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-28" data-testid="home-upcoming-events">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
            What's Next
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
            Upcoming <span className="italic text-electric text-pulse">events.</span>
          </h2>
        </div>
        <Link
          to="/events"
          data-testid="home-events-cta"
          className="inline-flex items-center gap-2 text-electric font-bold tracking-wide text-sm hover:gap-3 transition-all"
        >
          See all events <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6">
        {events.map((ev, i) => (
          <motion.div
            key={ev.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: Math.min(i * 0.08, 0.4) }}
            className="relative aspect-square overflow-hidden rounded-2xl bg-[#0a0a0a] border border-white/5 hover:border-electric/50 transition-all duration-300 group"
            data-testid={`home-event-card-${ev.id}`}
          >
            <Link to="/events" aria-label={ev.title} className="block w-full h-full">
              <img
                src={ev.image_url}
                alt={ev.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="home-page">
      <Seo
        title=""
        description="The Push Center — a church in Portsmouth, VA. Sundays 9 AM. Pastor Demetrial Donshae Hill. Out of Complacency Into Your Destiny."
        path="/"
      />

      {/* ROTATING HERO */}
      <RotatingHero />

      {/* THIS IS THE PUSH - PHOTO STRIP */}
      <section className="relative bg-black border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-6">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">
                This is The Push
              </div>
              <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">
                A family <span className="italic text-electric text-pulse">on fire.</span>
              </h2>
            </div>
            <Link
              to="/about"
              className="hidden sm:inline-flex items-center gap-2 text-electric font-bold text-sm hover:gap-3 transition-all"
              data-testid="home-photos-cta"
            >
              Meet the family <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 px-1 pb-1">
          {[
            { src: "/images/pulpit-banner.jpg", alt: "The Push Center sanctuary" },
            { src: "/images/pastor-preaching.jpg", alt: "Pastor preaching" },
            { src: "/images/worship-team.jpg", alt: "Worship team" },
            { src: "/images/congregation.jpg", alt: "Congregation" },
          ].map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="aspect-[4/5] overflow-hidden group relative"
              data-testid={`home-photo-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CONNECT WITH US — bring visitors into the loop early */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-24" data-testid="home-connect-cta">
        <div className="relative rounded-3xl overflow-hidden border border-electric/20 bg-gradient-to-br from-electric/15 via-[#0c0c0c] to-[#0c0c0c]">
          <div className="absolute inset-0 lightning-grid opacity-25 pointer-events-none" />
          <div className="relative p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-bold mb-3">
                Connect With Us
              </div>
              <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter leading-[1.05]">
                Stay in the <span className="italic text-electric">push.</span>
              </h2>
              <p className="mt-4 text-zinc-300 leading-relaxed text-base lg:text-lg">
                Follow us, watch sermons, see the latest from our family, and stay close to what God is doing here.
              </p>
            </div>
            <Link
              to="/connect"
              data-testid="home-cta-connect"
              className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-1 transition-all duration-300 whitespace-nowrap shadow-lg shadow-electric/30"
            >
              Connect With Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE TIMES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
              Gather With Us
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Times to <span className="italic text-electric text-pulse">connect.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Every gathering is an opportunity to be filled, to belong, and to be sent. Come as you
              are — for First Sunday communion, wear all black.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
            {[
              { title: "Sunday Worship", time: "9:00 AM", sub: "Every Sunday", icon: Heart },
              { title: "Sunday School", time: "8:00 AM", sub: "1st Sundays", icon: BookOpen },
              { title: "Bible Study", time: "7:30 PM", sub: "Wednesdays", icon: Zap },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="group relative p-7 rounded-2xl bg-[#0c0c0c] border border-white/5 hover:border-electric/50 transition-all duration-300"
                data-testid={`service-card-${i}`}
              >
                <item.icon className="w-7 h-7 text-electric mb-6" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  {item.sub}
                </div>
                <div className="font-display font-bold text-xl mb-2">{item.title}</div>
                <div className="font-display font-black text-3xl text-electric">{item.time}</div>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                     style={{boxShadow:"inset 0 0 60px rgba(34,197,94,0.08)"}} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <UpcomingEventsSection />

      {/* WELCOME / WHAT TO EXPECT */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#080808] to-black" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
              What to Expect
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-6">
              A welcoming, <span className="italic text-electric">spirit-filled</span> atmosphere.
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Powerful worship. Practical teaching. Real community. Whether it's your first time or
              you've been pursuing God for years — you belong here.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-8">
              We don't just gather. We build people. Come as you are, leave transformed.
            </p>
            <Link
              to="/about"
              data-testid="home-learn-more"
              className="inline-flex items-center gap-2 text-electric font-bold tracking-wide hover:gap-3 transition-all"
            >
              Learn our story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Users, label: "Belong", desc: "A family that sees you" },
              { icon: Heart, label: "Heal", desc: "Emotionally restored" },
              { icon: Zap, label: "Grow", desc: "Spiritually strong" },
              { icon: BookOpen, label: "Thrive", desc: "Living in purpose" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="p-6 rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/30 transition"
                data-testid={`expect-card-${i}`}
              >
                <item.icon className="w-6 h-6 text-electric mb-4" />
                <div className="font-display font-bold text-lg mb-1">{item.label}</div>
                <div className="text-sm text-zinc-400">{item.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEXTURED BAND — Need Prayer + Find Us share a single ambient backdrop */}
      <div className="relative noise overflow-hidden">
        {/* Texture layer 1 — subtle electric grid */}
        <div className="absolute -inset-y-12 inset-x-0 lightning-grid parallax-grid opacity-40 pointer-events-none" aria-hidden="true" />
        {/* Texture layer 2 — soft radial electric glow on the right side */}
        <div
          className="absolute -top-32 right-0 w-[60%] h-[80%] pointer-events-none"
          aria-hidden="true"
          style={{
            background: "radial-gradient(ellipse at right top, rgba(34,197,94,0.10), transparent 60%)",
          }}
        />
        {/* Texture layer 3 — diagonal accent line */}
        <div
          className="absolute inset-x-0 top-0 h-px pointer-events-none"
          aria-hidden="true"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.35), transparent)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
          aria-hidden="true"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.25), transparent)",
          }}
        />

        {/* CTA STRIP — NEED PRAYER */}
        <section className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="/images/need-prayer.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Dark overlay for text contrast */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
            <div className="absolute inset-0 bg-black/30" />
            {/* Subtle electric edge */}
            <div className="absolute inset-0 ring-1 ring-inset ring-electric/20 rounded-3xl pointer-events-none" />
            <div className="relative p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-bold mb-3">
                  Need Prayer?
                </div>
                <h3 className="font-display font-black text-3xl lg:text-5xl text-white tracking-tighter max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
                  Whatever you're facing, we'll stand with you.
                </h3>
              </div>
              <Link
                to="/contact"
                data-testid="home-cta-prayer"
                className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-1 transition-all duration-300 whitespace-nowrap shadow-lg shadow-electric/30"
              >
                Submit Prayer Request <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* LOCATION */}
        <section className="relative max-w-7xl mx-auto px-5 lg:px-10 pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
                Find Us
              </div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                6217 Portsmouth Blvd
              </h2>
              <p className="mt-3 text-zinc-400 text-lg">Portsmouth, Virginia 23701</p>
              <a
                href="https://maps.google.com/?q=6217+Portsmouth+Blvd+Portsmouth+VA+23701"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 text-electric hover:gap-3 transition-all font-bold"
                data-testid="home-get-directions"
              >
                <MapPin className="w-4 h-4" /> Get Directions <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <iframe
                  title="The Push Center Location"
                  src="https://www.google.com/maps?q=6217+Portsmouth+Blvd+Portsmouth+VA+23701&output=embed"
                  className="w-full h-full grayscale-[60%] contrast-110"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
